import React, {useEffect, useMemo, useRef} from "react";
import {useMap, AdvancedMarker} from "@vis.gl/react-google-maps";
import {isValid, parse} from "date-fns";
import GarrisonTimelineIcon from "../../timeline/components/garrison-timeline-icon.jsx";

const MAX_ROUTE_POINT_GAP_SECONDS = 120;

const isConnectedToPreviousPoint = (point) => {
  return point?.timeGap !== null && point?.timeGap !== undefined && point?.timeGap <= MAX_ROUTE_POINT_GAP_SECONDS;
};

const isIsolatedRoutePoint = (points, pointIndex) => {
  const point = points[pointIndex];
  const nextPoint = points[pointIndex + 1];

  return !isConnectedToPreviousPoint(point) && !isConnectedToPreviousPoint(nextPoint);
};

const parseFilterDate = (value) => {
  if (!value) return new Date();

  const parsedDateWithSeconds = parse(value, "dd/MM/yyyy HH:mm:ss", new Date());
  if (isValid(parsedDateWithSeconds)) return parsedDateWithSeconds;

  const parsedDateWithoutSeconds = parse(value, "dd/MM/yyyy HH:mm", new Date());
  if (isValid(parsedDateWithoutSeconds)) return parsedDateWithoutSeconds;

  const fallbackDate = new Date(value);
  return isValid(fallbackDate) ? fallbackDate : new Date();
};

const RoutePolyline = React.memo(function RoutePolyline({points}) {
  const map = useMap();

  useEffect(() => {
    const googleMaps = window.google?.maps;

    if (!map || !points || points.length === 0 || !googleMaps) return;

    const polylines = [];
    let currentSegment = [];
    let currentSegmentStatus = "AVAILABLE";

    const createPolyline = (path, status) => {
      const isDashed = ["DISPATCH", "ACTIVITY"].includes(status);

      return [new googleMaps.Polyline({
        path,
        geodesic: true,
        strokeColor: "#0082FF",
        strokeOpacity: isDashed ? 0 : 1,
        strokeWeight: 3,
        icons: isDashed ? [{
          icon: {
            path: "M 0,-1 0,1",
            strokeColor: "#0082FF",
            strokeOpacity: 1,
            scale: 3
          },
          offset: "0",
          repeat: "12px"
        }] : [],
        map
      })];
    };

    for (let i = 0; i < points.length; i++) {
      const currentPoint = points[i];
      const currentPointStatus = currentPoint.status === "OFFLINE"
        ? "OFFLINE"
        : !currentPoint.dispatchId
          ? "AVAILABLE"
          : currentPoint.dispatchActivity
            ? "ACTIVITY"
            : "DISPATCH";

      if (currentSegment.length === 0) {
        currentSegmentStatus = currentPointStatus;
      }

      const isTimeGap = currentPoint.timeGap > MAX_ROUTE_POINT_GAP_SECONDS;
      const isStatusChange = currentSegmentStatus !== currentPointStatus;

      if ((isTimeGap || isStatusChange) && currentSegment.length > 0) {
        polylines.push(...createPolyline(currentSegment, currentSegmentStatus));
        currentSegment = [];

        if (isStatusChange && !isTimeGap) {
          const prevPoint = points[i - 1];
          currentSegment.push({lat: prevPoint.latitude, lng: prevPoint.longitude});
        }

        currentSegmentStatus = currentPointStatus;
      }

      currentSegment.push({lat: currentPoint.latitude, lng: currentPoint.longitude});
    }

    if (currentSegment.length > 1) {
      polylines.push(...createPolyline(currentSegment, currentSegmentStatus));
    }

    return () => polylines.forEach(polyline => polyline.setMap(null));
  }, [map, points]);

  return null;
});

const GarrisonRouteMapContent = ({garrisonsList, timelineData, elapsedSeconds, filter, onActiveGarrisonsChange, isCropMode, cropRange}) => {
  const map = useMap();
  const hasFitBounds = useRef(false);

  useEffect(() => {
    hasFitBounds.current = false;
  }, [filter, timelineData]);

  useEffect(() => {
    if (!map || !timelineData || hasFitBounds.current || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidPoints = false;

    Object.values(timelineData).forEach(points => {
      if (!Array.isArray(points)) return;

      points.forEach(point => {
        const latitude = Number(point?.latitude);
        const longitude = Number(point?.longitude);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

        bounds.extend({lat: latitude, lng: longitude});
        hasValidPoints = true;
      });
    });

    if (hasValidPoints) {
      map.fitBounds(bounds, {bottom: 120});
      hasFitBounds.current = true;
    }
  }, [map, timelineData]);

  const routeLayers = useMemo(() => {
    if (!timelineData || !garrisonsList) return [];

    const dtStart = filter?.dtStart || new Date().toISOString();
    const baseStart = parseFilterDate(dtStart).getTime();

    return garrisonsList.map((garrison, index) => {
      const routePoints = timelineData[garrison.id];
      if (!routePoints || routePoints.length === 0) return null;

      const points = routePoints.map(point => {
        const pointTime = parse(point.time, "dd/MM/yyyy HH:mm:ss", new Date()).getTime();

        return {...point, timeInSeconds: (pointTime - baseStart) / 1000};
      });

      const visiblePoints = isCropMode && cropRange
        ? points.filter(point => point.timeInSeconds >= cropRange[0] && point.timeInSeconds <= cropRange[1])
        : points;

      if (visiblePoints.length === 0) return null;

      return {
        id: garrison.id,
        garrison,
        originalIndex: index,
        points: visiblePoints
      };
    }).filter(Boolean);
  }, [garrisonsList, timelineData, filter, isCropMode, cropRange]);

  const activeMarkersData = useMemo(() => {
    return routeLayers.map(route => {
      let prevPoint = route.points[0];
      let nextPoint = route.points[0];

      for (let pointIndex = 0; pointIndex < route.points.length; pointIndex++) {
        if (route.points[pointIndex].timeInSeconds <= elapsedSeconds) {
          prevPoint = route.points[pointIndex];
          nextPoint = route.points[pointIndex + 1] || route.points[pointIndex];
        } else {
          break;
        }
      }

      let currentLat = prevPoint.latitude;
      let currentLng = prevPoint.longitude;

      if (prevPoint !== nextPoint) {
        const timeDiff = nextPoint.timeInSeconds - prevPoint.timeInSeconds;

        if (timeDiff <= MAX_ROUTE_POINT_GAP_SECONDS && timeDiff > 0) {
          const progress = (elapsedSeconds - prevPoint.timeInSeconds) / timeDiff;
          currentLat = prevPoint.latitude + (nextPoint.latitude - prevPoint.latitude) * progress;
          currentLng = prevPoint.longitude + (nextPoint.longitude - prevPoint.longitude) * progress;
        }
      }

      return {
        ...prevPoint,
        id: route.id,
        lat: currentLat,
        lng: currentLng,
        garrison: route.garrison
      };
    }).filter(item => item.lat && item.lng);
  }, [routeLayers, elapsedSeconds]);

  const isolatedPoints = useMemo(() => {
    return routeLayers.flatMap(route => route.points
      .filter((point, pointIndex, points) => isIsolatedRoutePoint(points, pointIndex))
      .filter(point => point.latitude && point.longitude)
      .map(point => ({
        ...point,
        id: [route.id, point.time].join("-")
      }))
    );
  }, [routeLayers]);

  useEffect(() => {
    if (onActiveGarrisonsChange) {
      onActiveGarrisonsChange(routeLayers.map(route => route.id));
    }
  }, [routeLayers, onActiveGarrisonsChange]);

  return (
    <>
      {routeLayers.map(route => <RoutePolyline key={route.id} points={route.points} />)}
      {isolatedPoints.map(point => (
        <AdvancedMarker
          key={point.id}
          position={{lat: point.latitude, lng: point.longitude}}
          zIndex={500}
        >
          <div
            aria-label={["Ponto registrado em", point.time].join(" ")}
            className="h-3 w-3 rounded-full border-2 border-white bg-[#0082FF] shadow"
            title={["Ponto registrado em", point.time].join(" ")}
          />
        </AdvancedMarker>
      ))}
      {activeMarkersData.map(item => (
        <AdvancedMarker
          key={item.id}
          position={{lat: item.lat, lng: item.lng}}
          zIndex={1000}
          anchorPoint={["0px", "0px"]}
        >
          <div style={{transform: "translate(-50%, -50%)"}}>
            <GarrisonTimelineIcon i={item.garrison.orderIndex || 1} />
          </div>
        </AdvancedMarker>
      ))}
    </>
  );
};

export default GarrisonRouteMapContent;
